import { useLayoutEffect, useState } from 'react';

/** Marks MainLayout's scrollable content area — the viewport minus the left menu and the header. */
export const MAIN_CONTENT_AREA_ID = 'main-content-area';

interface ContentAreaRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

/**
 * Live viewport geometry of the main content area, so floating overlays can size themselves to the
 * content instead of the whole viewport. Re-measures on every resize of that area, which covers
 * collapsing the left menu, indicator banners appearing, and window resizes. Measured before paint
 * so consumers do not render one frame at a fallback size; null only if the layout is not mounted.
 */
export const useContentAreaRect = (): ContentAreaRect | null => {
    const [rect, setRect] = useState<ContentAreaRect | null>(null);

    useLayoutEffect(() => {
        const node = document.getElementById(MAIN_CONTENT_AREA_ID);
        if (!node) {
            return;
        }

        // The menu collapse animates, so identical measurements are dropped to avoid a render per frame.
        const measure = () => {
            const { left, top, width, height } = node.getBoundingClientRect();
            setRect((previous) =>
                previous?.left === left &&
                previous.top === top &&
                previous.width === width &&
                previous.height === height
                    ? previous
                    : { left, top, width, height }
            );
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    return rect;
};
